import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { alertManager } from '../services/AlertManager';

export interface Alert {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    action?: React.ReactNode;
}

interface AlertContextType {
    alerts: Alert[];
    addAlert: (type: 'success' | 'error' | 'warning' | 'info', message: string, action?: React.ReactNode) => void;
    removeAlert: (id: string) => void;
    clearAll: () => void;
    clearErrorAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

interface AlertProviderProps {
    children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const alertCounterRef = React.useRef(0);

    const addAlert = (
        type: 'success' | 'error' | 'warning' | 'info',
        message: string,
        action?: React.ReactNode
    ) => {
        // Use timestamp + counter to ensure unique IDs even if alerts are created in the same millisecond
        alertCounterRef.current += 1;
        const id = `${Date.now()}-${alertCounterRef.current}-${Math.random().toString(36).substr(2, 9)}`;

        // Remove any existing info alerts when adding a new alert
        setAlerts(prev => {
            const filteredAlerts = prev.filter(alert => alert.type !== 'info');
            return [...filteredAlerts, { id, type, message, action }];
        });

        // Auto-remove success alerts after 4 seconds (with fade starting at 3 seconds)
        if (type === 'success') {
            setTimeout(() => {
                removeAlert(id);
            }, 4000);
        }
    };

    const removeAlert = (id: string) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    };

    const clearAll = () => {
        setAlerts([]);
    };

    const clearErrorAlerts = () => {
        setAlerts(prev => prev.filter(alert => alert.type !== 'error'));
    };

    // Register with the alert manager
    useEffect(() => {
        alertManager.registerCallback((action, data) => {
            if (action === 'clearErrors') {
                clearErrorAlerts();
            } else if (action === 'addAlert' && data) {
                addAlert(data.type, data.message, data.action);
            }
        });
    }, []);

    return (
        <AlertContext.Provider value={{
            alerts,
            addAlert,
            removeAlert,
            clearAll,
            clearErrorAlerts,
        }}>
            {children}
        </AlertContext.Provider>
    );
};
